import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import CajaLayout from '../components/layout/CajaLayout'
import PaywallBanner from '../components/caja/PaywallBanner'
import SessionBookingCard from '../components/caja/SessionBookingCard'
import OnboardingTour, { useTourTrigger } from '../components/caja/OnboardingTour'
import TrialBadge from '../components/caja/TrialBadge'
import CajaOnboardingChat, { CajaOnboardingChatTrigger } from '../components/caja/CajaOnboardingChat'
import DemoBanner from '../components/caja/DemoBanner'
import EtapaFinalPaywall from '../components/caja/EtapaFinalPaywall'
import { exportStrategicPlanDocx, exportStrategicPlanPdf } from '../lib/exportPlan'
import { track } from '../lib/analytics'
import {
  ChevronRight, CheckCircle2, Circle, Clock, Lock,
  Sparkles, Target, TrendingUp, BarChart2, Lightbulb, Rocket, Building2, AlertCircle,
  FileText, Download, Loader, HelpCircle,
} from 'lucide-react'

// Phase metadata enrichment for the guided experience
const PHASE_META = {
  0: {
    icon: Building2,
    color: 'from-slate-500 to-slate-600',
    light: 'bg-slate-50 border-slate-200',
    badge: 'bg-slate-100 text-slate-600',
    description: 'Entiende cómo funciona tu negocio hoy: modelo, fuentes de ingreso y recursos clave.',
    tip: 'Empieza aquí. Entender el modelo de negocio actual es la base de toda estrategia.',
  },
  1: {
    icon: Target,
    color: 'from-blue-500 to-blue-600',
    light: 'bg-blue-50 border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
    description: 'Analiza el entorno externo (PESTEL), la industria (Porter), lo interno y construye tu DOFA.',
    tip: 'Esta etapa revela dónde están las amenazas y oportunidades reales de tu sector.',
  },
  2: {
    icon: Lightbulb,
    color: 'from-violet-500 to-violet-600',
    light: 'bg-violet-50 border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    description: 'Define (o redefine) tu misión, visión, valores y los objetivos estratégicos de los próximos 3 años.',
    tip: 'Aquí se toman las decisiones más importantes. Tómate el tiempo necesario.',
  },
  3: {
    icon: BarChart2,
    color: 'from-cyan-500 to-cyan-600',
    light: 'bg-cyan-50 border-cyan-200',
    badge: 'bg-cyan-100 text-cyan-700',
    description: 'Mide el tamaño del mercado, analiza productos, canales y la participación de tus competidores.',
    tip: 'Los datos de mercado convierten suposiciones en decisiones basadas en evidencia.',
  },
  4: {
    icon: TrendingUp,
    color: 'from-emerald-500 to-emerald-600',
    light: 'bg-emerald-50 border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    description: 'Define la estrategia específica para cada unidad de negocio: posicionamiento, portafolio y precio.',
    tip: 'Cada unidad de negocio puede requerir una estrategia diferente. Aquí las distingues.',
  },
  5: {
    icon: Rocket,
    color: 'from-orange-500 to-orange-600',
    light: 'bg-orange-50 border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    description: 'Traduce la estrategia en un plan de implementación con responsables, fechas y KPIs.',
    tip: 'Una estrategia sin plan de ejecución es solo un deseo. Este es el paso que lo hace real.',
  },
  6: {
    icon: Sparkles,
    color: 'from-brand-600 to-brand-700',
    light: 'bg-brand-50 border-brand-200',
    badge: 'bg-brand-100 text-brand-700',
    description: 'Consolida todas las unidades en una narrativa corporativa integrada y coherente.',
    tip: 'El resultado final: una estrategia corporativa unificada lista para presentar a tu equipo.',
  },
}

const STATUS_CONFIG = {
  pending:     { icon: Circle,       label: 'Por empezar',  className: 'text-gray-300' },
  in_progress: { icon: Clock,        label: 'En progreso',  className: 'text-blue-500' },
  review:      { icon: Clock,        label: 'En revisión',  className: 'text-violet-500' },
  completed:   { icon: CheckCircle2, label: 'Completada',   className: 'text-emerald-500' },
  blocked:     { icon: Lock,         label: 'Bloqueada',    className: 'text-red-400' },
}

export default function CajaDashboardPage({ forceDemo = false }) {
  const { profile }     = useAuth()
  const navigate        = useNavigate()
  const [searchParams]  = useSearchParams()
  const [consultancy, setConsultancy]         = useState(null)
  const [phases, setPhases]                   = useState([])
  const [loading, setLoading]                 = useState(true)
  const [subscriptionStatus, setSubscriptionStatus] = useState(null) // null = loading
  const [sessionCredits, setSessionCredits]   = useState(0)
  const [paymentFeedback, setPaymentFeedback] = useState(null) // 'success' | 'failure' | 'pending'
  const [exporting, setExporting]             = useState(null) // 'docx' | 'pdf' | null
  const [exportError, setExportError]         = useState('')
  const [tourTrigger, openTour]               = useTourTrigger()
  const [businessUnits, setBusinessUnits]     = useState([])
  const [ficha, setFicha]                     = useState(null)         // project_ficha (Etapa 0 status)
  const [paywallOpen, setPaywallOpen]         = useState(false)        // hard-paywall modal triggered by locked-phase click
  // Chat IA onboarding: abrir automáticamente si no se cerró y aún tiene mensajes
  const [showOnboardingChat, setShowOnboardingChat] = useState(false)
  useEffect(() => {
    if (!profile) return
    const done = !!profile.caja_onboarding_done_at
    const used = profile.caja_onboarding_messages_used ?? 0
    // Abrir automáticamente si el usuario está fresh, no lo cerró y tiene cuota
    if (!done && used < 10) setShowOnboardingChat(true)
  }, [profile?.caja_onboarding_done_at, profile?.caja_onboarding_messages_used])

  useEffect(() => { fetchData() }, [profile?.id])

  // Handle MercadoPago return URL params (?payment=success|failure|pending)
  useEffect(() => {
    const result = searchParams.get('payment')
    const plan   = searchParams.get('plan')
    if (result) {
      setPaymentFeedback(result)
      track('caja_payment_return', { result, plan_id: plan })
    }
  }, [searchParams])

  async function fetchData() {
    if (!profile?.id) return
    setLoading(true)

    // Load subscription status from profile
    const { data: prof } = await supabase
      .from('profiles')
      .select('subscription_status, subscription_expires_at, session_credits_remaining')
      .eq('id', profile.id)
      .single()

    // Determine effective status
    let subStatus = prof?.subscription_status ?? 'trial'
    if (subStatus === 'active' && prof?.subscription_expires_at) {
      if (new Date(prof.subscription_expires_at) < new Date()) subStatus = 'expired'
    }
    setSubscriptionStatus(subStatus)
    setSessionCredits(prof?.session_credits_remaining ?? 0)

    // ── Modo demo: fetch la consultancy demo en vez de la del usuario ─────
    let targetConsultancyId
    if (forceDemo) {
      const { data: demoCons } = await supabase
        .from('consultancies')
        .select('id')
        .eq('is_demo', true)
        .limit(1)
        .maybeSingle()
      targetConsultancyId = demoCons?.id
    } else {
      // Find user's caja consultancy
      const { data: link } = await supabase
        .from('consultancy_users')
        .select('consultancy_id')
        .eq('user_id', profile.id)
        .limit(1)
        .maybeSingle()
      targetConsultancyId = link?.consultancy_id
    }

    if (!targetConsultancyId) { setLoading(false); return }
    const link = { consultancy_id: targetConsultancyId } // shim for code below

    // Promise.allSettled — UNA query rota no debe tumbar todo el dashboard
    const results = await Promise.allSettled([
      supabase.from('consultancies').select('*').eq('id', link.consultancy_id).single(),
      supabase.from('phases')
        .select('id, name, phase_order, status, progress, sections(id, name, status)')
        .eq('consultancy_id', link.consultancy_id)
        .is('business_unit_id', null)
        .order('phase_order'),
      supabase.from('business_units')
        .select('id, name, description, bu_order, phases(id, status, sections(id, status))')
        .eq('consultancy_id', link.consultancy_id)
        .order('bu_order'),
      supabase.from('project_ficha')
        .select('*')
        .eq('consultancy_id', link.consultancy_id)
        .maybeSingle(),
    ])
    const pick = (idx) => results[idx]?.status === 'fulfilled' ? results[idx].value?.data : null
    setConsultancy(pick(0))
    setPhases(pick(1) ?? [])
    setBusinessUnits(pick(2) ?? [])
    setFicha(pick(3) ?? null)
    results.forEach((r, i) => {
      if (r.status === 'rejected') console.warn('[CajaDashboard query failed]', i, r.reason)
    })
    setLoading(false)
  }

  // ── Trial gating helpers ───────────────────────────────────────────────────
  // En trial: solo Etapa 0 está disponible. El resto exige suscripción.
  // Excepción: si ya completó el informe IA, mostramos celebración + paywall.
  const isTrial = subscriptionStatus === 'trial' || subscriptionStatus === null
  const isPaid  = subscriptionStatus === 'active'
  // Después del refactor a diálogo iterativo, "Etapa 0 cerrada" = tener el
  // informe FINAL (no el preliminar de la ronda 1).
  const etapa0Done = !!ficha?.etapa0_final_report_generated_at
  // ¿El usuario ya escribió algo en Etapa 0?
  const etapa0HasContent = !!(
    ficha?.bm_descripcion_libre ||
    ficha?.bm_que_vendes ||
    ficha?.bm_a_quien ||
    ficha?.bm_como_entregas ||
    ficha?.bm_como_ganas_dinero ||
    ficha?.bm_que_te_hace_unico ||
    ficha?.bm_supuestos
  )

  // ¿Hay progreso en alguna fase? (suma de secciones completadas en cualquier etapa)
  const hasAnyPhaseProgress = phases.some(p =>
    (p.sections ?? []).some(s => s.status === 'completed' || s.status === 'in_progress')
  )

  // Estado del usuario para progressive disclosure:
  //   'fresh'        — recién llegó, no ha tocado nada → bienvenida grande, single CTA
  //   'in_etapa0'    — ya empezó a llenar Etapa 0 → roadmap visible, soft paywall
  //   'etapa0_done'  — generó informe IA, en trial → celebración + paywall reforzado
  //   'paid'         — suscripción activa → full dashboard
  const userStage =
    isPaid                                       ? 'paid' :
    etapa0Done                                   ? 'etapa0_done' :
    etapa0HasContent || hasAnyPhaseProgress      ? 'in_etapa0' :
                                                   'fresh'

  function isPhaseLocked(phase) {
    return isTrial && phase.phase_order > 0
  }

  function handlePhaseClick(phase) {
    if (isPhaseLocked(phase)) {
      track('caja_locked_phase_clicked', { phase_order: phase.phase_order })
      setPaywallOpen(true)
      return
    }
    navigate(`/consultancy/${consultancy.id}/phase/${phase.id}`)
  }

  function handleStartEtapa0() {
    const etapa0 = phases.find(p => p.phase_order === 0)
    if (etapa0) navigate(`/consultancy/${consultancy.id}/phase/${etapa0.id}`)
  }

  // Compute overall progress
  const totalSections = phases.reduce((s, p) => s + (p.sections?.length ?? 0), 0)
  const doneSections  = phases.reduce((s, p) => s + (p.sections?.filter(sec => sec.status === 'completed').length ?? 0), 0)
  const overallPct    = totalSections > 0 ? Math.round((doneSections / totalSections) * 100) : 0

  // Find the first non-completed phase (next step)
  const nextPhase = phases.find(p => p.status !== 'completed') ?? null

  async function handleExport(format, variant = 'full') {
    if (!consultancy?.id) return
    setExporting(`${format}:${variant}`)
    setExportError('')
    track('caja_plan_export_started', { format, variant, progress_pct: overallPct })
    try {
      if (format === 'docx') await exportStrategicPlanDocx(consultancy.id, variant)
      else                   await exportStrategicPlanPdf(consultancy.id, variant)
      track('caja_plan_export_completed', { format, variant })
    } catch (err) {
      console.error(err)
      setExportError(err.message ?? 'No se pudo generar el archivo. Intenta de nuevo.')
      track('caja_plan_export_failed', { format, variant, error: err.message })
    } finally {
      setExporting(null)
    }
  }

  const canExport = subscriptionStatus === 'active' || subscriptionStatus === 'trial'

  if (loading) return (
    <CajaLayout>
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-600 border-t-transparent" />
      </div>
    </CajaLayout>
  )

  if (!consultancy) return (
    <CajaLayout>
      <div className="text-center py-24 text-gray-400">
        <p className="text-sm">No encontramos tu proyecto. Contáctanos a estrategia@nextvalleyco.com</p>
      </div>
    </CajaLayout>
  )

  return (
    <CajaLayout>
      {/* ── Hard paywall modal for expired subscriptions ── */}
      {subscriptionStatus === 'expired' && <PaywallBanner isTrial={false} />}

      {/* ── Hard paywall modal triggered by clicking a locked phase ── */}
      {paywallOpen && (
        <div onClick={() => setPaywallOpen(false)}>
          <PaywallBanner isTrial={false} />
        </div>
      )}

      {/* ── First-visit onboarding tour ── */}
      <OnboardingTour
        companyName={consultancy?.client_company}
        nextPhaseName={nextPhase?.name}
        triggerKey={tourTrigger}
      />

      <div className="space-y-8">

        {/* ── Banner DEMO si el usuario está navegando la consultancy ejemplo ── */}
        {consultancy?.is_demo && (
          <DemoBanner onSubscribe={() => setPaywallOpen(true)} />
        )}

        {/* ── Payment feedback banner ── */}
        {paymentFeedback === 'success' && (
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-emerald-800 text-sm font-medium">
            <CheckCircle2 size={18} className="text-emerald-500 flex-shrink-0" />
            ¡Pago confirmado! Tu suscripción está activa. Bienvenido/a al plan completo.
          </div>
        )}
        {paymentFeedback === 'failure' && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-800 text-sm">
            <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
            El pago no pudo procesarse. Intenta de nuevo o escríbenos a estrategia@nextvalleyco.com
          </div>
        )}
        {paymentFeedback === 'pending' && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-800 text-sm">
            <Clock size={18} className="text-amber-500 flex-shrink-0" />
            Tu pago está siendo procesado. Te notificaremos cuando se confirme.
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            ESTADO 'fresh' — Usuario recién llegado.
            Vista MUY simple: chat IA onboarding + bienvenida + CTA grande.
            Oculta downloads, sesiones, BUs y "talk to consultant".
            ════════════════════════════════════════════════════════════════ */}
        {userStage === 'fresh' && (
          <>
            {showOnboardingChat && (
              <CajaOnboardingChat
                companyName={consultancy.client_company}
                industry={consultancy.industry}
                onClose={() => setShowOnboardingChat(false)}
                onStartEtapa0={handleStartEtapa0}
              />
            )}
            <FreshWelcome
              userName={profile?.full_name?.split(' ')[0]}
              companyName={consultancy.client_company}
              industry={consultancy.industry}
              onStart={handleStartEtapa0}
              showChatTrigger={!showOnboardingChat}
              hasUsedChat={(profile?.caja_onboarding_messages_used ?? 0) > 0}
              onOpenChat={() => setShowOnboardingChat(true)}
            />
          </>
        )}

        {/* ── Trial soft banner (solo cuando ya empezó) ── */}
        {userStage === 'in_etapa0' && (
          <PaywallBanner isTrial={true} />
        )}

        {/* ── Etapa 0 cerrada — paywall unificado con preview de 6 etapas ── */}
        {isTrial && etapa0Done && (
          <EtapaFinalPaywall
            report={ficha?.etapa0_final_report}
            ficha={ficha}
            consultancyId={consultancy.id}
            companyName={consultancy.client_company}
          />
        )}

        {/* ── (legacy bloque ya reemplazado por EtapaFinalPaywall) ── */}
        {false && isTrial && etapa0Done && (
          <div className="rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-violet-50 to-amber-50 p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-700 flex items-center justify-center flex-shrink-0">
                <Sparkles size={22} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-gray-900 mb-1">
                  🎉 ¡Etapa 0 completada! Recibiste tu informe ejecutivo gratis.
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  La IA leyó tu modelo de negocio y generó un diagnóstico inicial.
                  Lo que sigue es donde realmente está el valor: análisis de mercado,
                  competencia, posicionamiento y plan estratégico personalizado.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                  {[
                    { icon: '🔍', label: 'Diagnóstico Estratégico (PESTEL + Porter + DOFA)' },
                    { icon: '🧭', label: 'Definición Estratégica (Misión, Visión, Objetivos)' },
                    { icon: '📊', label: 'Análisis del Mercado (tamaño, canales, competencia)' },
                    { icon: '🎯', label: 'Estrategia por Unidad de Negocio' },
                    { icon: '🚀', label: 'Plan de Implementación con KPIs' },
                    { icon: '🌟', label: 'Plan Corporativo Consolidado' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-gray-700 bg-white/60 rounded-lg px-3 py-2">
                      <span className="flex-shrink-0">{item.icon}</span>
                      <span className="leading-relaxed">{item.label}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setPaywallOpen(true)}
                    className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all shadow"
                  >
                    <Sparkles size={14} /> Activar suscripción para continuar
                  </button>
                  <button
                    onClick={() => {
                      const etapa0 = phases.find(p => p.phase_order === 0)
                      if (etapa0) navigate(`/consultancy/${consultancy.id}/phase/${etapa0.id}?tab=mi_modelo`)
                    }}
                    className="flex items-center gap-2 bg-white/80 hover:bg-white border border-gray-200 text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
                  >
                    <FileText size={14} /> Ver mi informe
                  </button>
                  <button
                    onClick={() => navigate('/caja/demo')}
                    className="flex items-center gap-2 bg-white/80 hover:bg-white border border-gray-200 text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
                  >
                    <FileText size={14} /> Ver plan ejemplo
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Welcome (sólo se muestra fuera del estado 'fresh') ── */}
        {userStage !== 'fresh' && (
          <>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-gray-400 mb-0.5">Bienvenido/a, {profile?.full_name?.split(' ')[0]}</p>
            <h1 className="text-2xl font-bold text-gray-900">{consultancy.client_company}</h1>
            <p className="text-sm text-gray-400 mt-1">{consultancy.industry}</p>
          </div>
          {/* Progress ring + tour button */}
          <div className="flex items-center gap-2">
            <button
              onClick={openTour}
              className="p-2 rounded-xl border border-gray-200 bg-white text-gray-400 hover:text-brand-700 hover:border-brand-200 transition-colors"
              title="Volver a ver el tour"
              aria-label="Reabrir tour"
            >
              <HelpCircle size={16} />
            </button>
            <div className="flex items-center gap-4 bg-white rounded-2xl px-5 py-3 border border-gray-100 shadow-sm">
              <ProgressRing pct={overallPct} size={56} stroke={5} />
              <div>
                <p className="text-2xl font-bold text-gray-800">{overallPct}%</p>
                <p className="text-xs text-gray-400">completado</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Next step banner ── */}
        {nextPhase && (
          <div
            className={`rounded-2xl bg-gradient-to-r ${PHASE_META[nextPhase.phase_order]?.color ?? 'from-brand-600 to-brand-700'} p-5 text-white cursor-pointer hover:shadow-lg transition-shadow`}
            onClick={() => navigate(`/consultancy/${consultancy.id}/phase/${nextPhase.id}`)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles size={14} className="opacity-80" />
                  <span className="text-xs font-semibold uppercase tracking-wide opacity-80">Siguiente paso</span>
                </div>
                <h2 className="text-lg font-bold mb-1">{nextPhase.name}</h2>
                <p className="text-sm opacity-80 leading-relaxed">
                  {PHASE_META[nextPhase.phase_order]?.description}
                </p>
              </div>
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <ChevronRight size={20} />
              </div>
            </div>
            {PHASE_META[nextPhase.phase_order]?.tip && (
              <div className="mt-3 pt-3 border-t border-white/20 text-xs opacity-70 flex items-start gap-1.5">
                <Lightbulb size={12} className="mt-0.5 flex-shrink-0" />
                {PHASE_META[nextPhase.phase_order].tip}
              </div>
            )}
          </div>
        )}

        {/* ── Phase roadmap ── */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">Tu hoja de ruta</h2>
          <div className="space-y-3">
            {phases.map((phase, idx) => {
              const meta       = PHASE_META[phase.phase_order] ?? {}
              const PhaseIcon  = meta.icon ?? Target
              const statusCfg  = STATUS_CONFIG[phase.status] ?? STATUS_CONFIG.pending
              const StatusIcon = statusCfg.icon
              const sections   = phase.sections ?? []
              const doneSecs   = sections.filter(s => s.status === 'completed').length
              const reviewSecs = sections.filter(s => s.status === 'review').length
              const inProgSecs = sections.filter(s => s.status === 'in_progress').length
              const blockSecs  = sections.filter(s => s.status === 'blocked').length
              const pendSecs   = sections.filter(s => !s.status || s.status === 'pending').length
              // Weighted: completed=1, review=0.85, in_progress=0.5
              const pct = sections.length > 0
                ? Math.round(((doneSecs * 1) + (reviewSecs * 0.85) + (inProgSecs * 0.5)) / sections.length * 100)
                : 0
              const widthOf = n => sections.length > 0 ? `${(n / sections.length) * 100}%` : '0%'
              const isNext     = nextPhase?.id === phase.id
              const isPast     = phase.status === 'completed'
              const locked     = isPhaseLocked(phase)

              return (
                <div
                  key={phase.id}
                  className={`rounded-xl border p-4 transition-all hover:shadow-md ${
                    locked   ? 'bg-gray-50 border-gray-200 cursor-pointer opacity-90 hover:border-indigo-200' :
                    isNext   ? `${meta.light ?? 'bg-brand-50 border-brand-200'} shadow-sm cursor-pointer` :
                    isPast   ? 'bg-gray-50 border-gray-100 opacity-80 cursor-pointer' :
                               'bg-white border-gray-100 hover:border-gray-200 cursor-pointer'
                  }`}
                  onClick={() => handlePhaseClick(phase)}
                >
                  <div className="flex items-center gap-3">
                    {/* Index / icon */}
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      locked ? 'bg-gray-200' :
                      isPast ? 'bg-emerald-100' :
                      meta.badge ?? 'bg-gray-100'
                    }`}>
                      {locked
                        ? <Lock size={16} className="text-gray-400" />
                        : isPast
                          ? <CheckCircle2 size={18} className="text-emerald-500" />
                          : <PhaseIcon size={17} className={isNext ? 'text-gray-700' : 'text-gray-400'} />
                      }
                    </div>

                    {/* Name + sections */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-semibold text-sm truncate ${
                          locked ? 'text-gray-500' :
                          isNext ? 'text-gray-900' :
                          isPast ? 'text-gray-500' :
                                   'text-gray-600'
                        }`}>
                          {phase.name}
                        </span>
                        {locked && (
                          <span className="text-[11px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0 inline-flex items-center gap-1">
                            <Lock size={10} /> Suscríbete para desbloquear
                          </span>
                        )}
                        {!locked && isNext && (
                          <span className="text-xs bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                            Continuar
                          </span>
                        )}
                      </div>
                      {sections.length > 0 && (
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          {doneSecs > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {doneSecs}
                            </span>
                          )}
                          {reviewSecs > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-violet-500" /> {reviewSecs}
                            </span>
                          )}
                          {inProgSecs > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> {inProgSecs}
                            </span>
                          )}
                          {blockSecs > 0 && (
                            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {blockSecs}
                            </span>
                          )}
                          <span className="text-[11px] text-gray-400">de {sections.length}</span>
                        </div>
                      )}
                    </div>

                    {/* Status + multi-color progress bar */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {sections.length > 0 && pct > 0 && !isPast && (
                        <div className="hidden sm:flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden flex">
                            <div className="bg-emerald-500 h-full transition-all" style={{ width: widthOf(doneSecs) }} />
                            <div className="bg-violet-500 h-full transition-all"  style={{ width: widthOf(reviewSecs) }} />
                            <div className="bg-blue-500 h-full transition-all"    style={{ width: widthOf(inProgSecs) }} />
                            <div className="bg-red-400 h-full transition-all"     style={{ width: widthOf(blockSecs) }} />
                          </div>
                          <span className="text-xs text-gray-400 tabular-nums">{pct}%</span>
                        </div>
                      )}
                      <StatusIcon size={17} className={statusCfg.className} />
                      <ChevronRight size={15} className="text-gray-300" />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
          </>
        )}

        {/* ── Business Units (auto-creadas desde Selección de Mercados en E3)
            Solo visible cuando hay suscripción activa, ya que las BUs solo
            existen después de la Selección de Mercados en Etapa 3. ── */}
        {userStage === 'paid' && businessUnits.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Tus Mercados / Unidades de Negocio
              </h2>
              <span className="text-xs text-gray-400">
                {businessUnits.length} mercado{businessUnits.length === 1 ? '' : 's'} priorizado{businessUnits.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {businessUnits.map(bu => {
                const buPhases = bu.phases ?? []
                const buSections = buPhases.flatMap(p => p.sections ?? [])
                const buDone = buSections.filter(s => s.status === 'completed').length
                const buPct = buSections.length > 0 ? Math.round((buDone / buSections.length) * 100) : 0
                return (
                  <div
                    key={bu.id}
                    onClick={() => navigate(`/consultancy/${consultancy.id}/bu/${bu.id}`)}
                    className="rounded-xl border border-gray-200 bg-white p-4 hover:border-brand-300 hover:shadow-md cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">{bu.name}</h3>
                        {bu.description && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{bu.description}</p>
                        )}
                      </div>
                      <ChevronRight size={16} className="text-gray-300 flex-shrink-0 mt-1" />
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-brand-500 rounded-full transition-all" style={{ width: `${buPct}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 tabular-nums w-9 text-right">{buPct}%</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-2">
                      {buPhases.length} etapas · {buDone}/{buSections.length} secciones
                    </p>
                  </div>
                )
              })}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Cada mercado tiene su propio análisis profundo (mercado, consumidor, competidores, interno, estrategia, consolidación). Si quieres añadir más mercados, vuelve a la sección <strong>Selección de Mercados</strong> en Etapa 3.
            </p>
          </div>
        )}

        {/* ── Download plan deliverable (solo visible cuando hay datos
            significativos para descargar — en trial los archivos serían
            casi vacíos, y el "Ver mi informe" del banner de celebración ya
            cumple el rol de descarga preliminar). ── */}
        {userStage === 'paid' && canExport && (
          <div className="rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 to-white p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={16} className="text-brand-700" />
                  <h3 className="text-base font-bold text-gray-900">Descarga tu plan estratégico</h3>
                </div>
                <p className="text-sm text-gray-500 max-w-md leading-relaxed">
                  El archivo consolida las 7 etapas con tu contenido y los informes generados por el agente de IA. Elige una versión:
                </p>
                {exportError && <p className="text-xs text-red-600 mt-2">{exportError}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Plan Completo */}
              <div className="rounded-xl border-2 border-brand-200 bg-white p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold uppercase tracking-wide text-brand-700">Plan completo</span>
                  <span className="text-[10px] bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded font-medium">Recomendado</span>
                </div>
                <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                  Cover · TOC · resumen · contenido de las 7 etapas · datos de mercado · objetivos BSC · unidades de negocio.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExport('docx', 'full')}
                    disabled={exporting !== null}
                    className="flex-1 flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-60 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  >
                    {exporting === 'docx:full'
                      ? <><Loader size={12} className="animate-spin" /> Generando…</>
                      : <><Download size={12} /> Word</>}
                  </button>
                  <button
                    onClick={() => handleExport('pdf', 'full')}
                    disabled={exporting !== null}
                    className="flex-1 flex items-center justify-center gap-2 border border-brand-700 text-brand-700 hover:bg-brand-50 disabled:opacity-60 text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  >
                    {exporting === 'pdf:full'
                      ? <><Loader size={12} className="animate-spin" /> Generando…</>
                      : <><Download size={12} /> PDF</>}
                  </button>
                </div>
              </div>

              {/* Resumen Ejecutivo */}
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-700">Resumen ejecutivo</span>
                </div>
                <p className="text-xs text-gray-500 mb-3 leading-relaxed">
                  Versión corta para presentar a la junta o socios: cover · resumen por etapa · datos clave · unidades de negocio. Sin contenido por sección.
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleExport('docx', 'executive')}
                    disabled={exporting !== null}
                    className="flex-1 flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-800 disabled:opacity-60 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  >
                    {exporting === 'docx:executive'
                      ? <><Loader size={12} className="animate-spin" /> Generando…</>
                      : <><Download size={12} /> Word</>}
                  </button>
                  <button
                    onClick={() => handleExport('pdf', 'executive')}
                    disabled={exporting !== null}
                    className="flex-1 flex items-center justify-center gap-2 border border-gray-700 text-gray-700 hover:bg-gray-50 disabled:opacity-60 text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  >
                    {exporting === 'pdf:executive'
                      ? <><Loader size={12} className="animate-spin" /> Generando…</>
                      : <><Download size={12} /> PDF</>}
                  </button>
                </div>
              </div>
            </div>

            {subscriptionStatus === 'trial' && (
              <p className="text-xs text-amber-700 mt-3 flex items-center gap-1.5">
                <AlertCircle size={12} />
                En modo trial, los archivos llevan una marca de "BORRADOR · TRIAL". Activa tu suscripción para retirarla.
              </p>
            )}
          </div>
        )}

        {/* ── Session booking + packs (solo visible cuando ya pagó la
            suscripción o cuando ya completó Etapa 0 — en ese punto el
            usuario está committed y comprar sesiones tiene sentido). ── */}
        {(userStage === 'paid' || userStage === 'etapa0_done') && (
          <SessionBookingCard
            creditsRemaining={sessionCredits}
            consultancyId={consultancy.id}
          />
        )}

        {/* ── CTA: upgrade to full consulting (solo paid) ── */}
        {userStage === 'paid' && (
          <div className="rounded-2xl bg-gradient-to-r from-brand-700 to-brand-900 p-6 text-white text-center">
            <h3 className="font-bold text-lg mb-1">¿Quieres ir más lejos?</h3>
            <p className="text-brand-200 text-sm mb-4 leading-relaxed">
              Nuestros consultores pueden acompañarte presencialmente a implementar este plan estratégico.
            </p>
            <a
              href="mailto:estrategia@nextvalleyco.com?subject=Quiero consultoría completa"
              className="inline-flex items-center gap-2 bg-white text-brand-700 font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-brand-50 transition-colors"
            >
              <Sparkles size={15} /> Hablar con un consultor NVC
            </a>
          </div>
        )}

      </div>
    </CajaLayout>
  )
}

// ── FreshWelcome ──────────────────────────────────────────────────────────────
// Vista de bienvenida para el usuario que recién entra. Sustituye TODA la
// dashboard hasta que él hace click en "Empezar". Progressive disclosure:
// no abrumar con downloads, sesiones y opciones avanzadas.
function FreshWelcome({ userName, companyName, industry, onStart, showChatTrigger = false, hasUsedChat = false, onOpenChat }) {
  const ETAPAS = [
    { num: 0, title: 'Modelo de Negocio',         hint: 'Cuéntanos cómo funciona tu empresa hoy. Empiezas aquí.', highlight: true },
    { num: 1, title: 'Diagnóstico Estratégico',   hint: 'Análisis externo (PESTEL), industria (Porter), interno y DOFA.' },
    { num: 2, title: 'Definición Estratégica',    hint: 'Misión, visión, objetivos y mapa estratégico (BSC).' },
    { num: 3, title: 'Análisis del Mercado',      hint: 'Tamaño, productos, canales y competidores.' },
    { num: 4, title: 'Estrategia por Unidad',     hint: 'Posicionamiento, portafolio y precio por mercado.' },
    { num: 5, title: 'Plan de Implementación',    hint: 'Pilares, actividades, KPIs y responsables.' },
    { num: 6, title: 'Plan Corporativo',          hint: 'Consolidación final lista para presentar.' },
  ]
  return (
    <div className="space-y-6">
      {/* Bienvenida */}
      <div className="rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-700 to-brand-800 p-8 md:p-10 text-white shadow-lg">
        <p className="text-indigo-200 text-sm font-medium mb-2">Hola, {userName ?? 'bienvenido/a'} 👋</p>
        <h1 className="text-2xl md:text-3xl font-bold leading-tight mb-3">
          Bienvenido a tu Plan Estratégico en una Caja
        </h1>
        <p className="text-indigo-100 text-sm md:text-base leading-relaxed max-w-2xl">
          Vas a construir el plan estratégico de <strong>{companyName ?? 'tu empresa'}</strong> paso a paso,
          guiado por la metodología <strong>Next Valley Consultants</strong> y un agente de IA que te acompaña
          en cada decisión. El proceso completo te toma entre 4 y 6 semanas.
        </p>
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <button
            onClick={onStart}
            className="inline-flex items-center gap-2 bg-white text-brand-800 hover:bg-cream-100 font-bold text-sm md:text-base px-6 py-3 rounded-xl transition-colors shadow-sm"
          >
            <Sparkles size={16} /> Empezar — Modelo de Negocio
            <ChevronRight size={18} />
          </button>
          {showChatTrigger && onOpenChat && (
            <CajaOnboardingChatTrigger onOpen={onOpenChat} hasUsedChat={hasUsedChat} />
          )}
          <span className="text-xs text-indigo-200">
            Tarda ~30-45 minutos · Recibes un informe ejecutivo IA al terminar
          </span>
        </div>
      </div>

      {/* Roadmap visual de las 7 etapas */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">El proceso completo</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ETAPAS.map(et => (
            <div
              key={et.num}
              className={`rounded-xl border p-4 transition-all ${
                et.highlight
                  ? 'border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-white shadow-sm'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  et.highlight ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  {et.num}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-semibold text-sm ${et.highlight ? 'text-gray-900' : 'text-gray-700'}`}>
                      {et.title}
                    </h3>
                    {et.highlight && (
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-indigo-600 text-white px-2 py-0.5 rounded-full">
                        Empieza aquí
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{et.hint}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trial countdown (cuenta regresiva con barra) */}
      <TrialBadge variant="hero" />

      {/* Trial subtle reminder */}
      <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/60 px-5 py-4">
        <Sparkles size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="text-gray-800 font-semibold">Estás en modo prueba gratuita</p>
          <p className="text-gray-600 text-xs mt-0.5 leading-relaxed">
            Puedes completar la <strong>Etapa 0 — Modelo de Negocio</strong> sin pagar.
            Al terminar, recibes un informe ejecutivo gratis hecho por la IA.
            Para continuar con las siguientes 6 etapas necesitas suscribirte.
          </p>
        </div>
      </div>
    </div>
  )
}

// ── Progress ring SVG ────────────────────────────────────────────────────────
function ProgressRing({ pct, size = 56, stroke = 5 }) {
  const r      = (size - stroke) / 2
  const circ   = 2 * Math.PI * r
  const offset = circ - (pct / 100) * circ
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="#1e3a5f" strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" className="transition-all duration-700"
      />
    </svg>
  )
}
